import { router } from '@/app/providers';
import { levelsColors } from '@/entities/facility/ui/facility-levels-of-care-table';
import type { FacilityLevelsOfCareDto } from '@/shared/api/modules/facilities';
import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/currency';
import { Table, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import type { FC } from 'react';
import { getFacilityRatesAndRevenueQueryOptions } from '../api/get-facility-rates-and-revenue.query';
import type { RatesAndRevenueTableColumns } from '../model/types';

const columnHelper = createColumnHelper<RatesAndRevenueTableColumns>();

const columns = [
  columnHelper.accessor('levelOfCare', {
    enableSorting: false,
    header: 'Care Level',
    maxSize: 200,
    cell: ({ getValue }) => {
      const value = getValue();

      return (
        <div className="flex items-center gap-2">
          <span className={cn('h-1.5 w-1.5', levelsColors[value - 1])} />
          <Typography>Level of Care {value}</Typography>
        </div>
      );
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('highestRate', {
    enableSorting: false,
    header: 'Highest Rate',
    cell: ({ getValue }) => {
      const { rate, fullName, residentId } = getValue();

      return (
        <div>
          <Typography
            className={cn('opacity-85', residentId && 'hover:cursor-pointer')}
            onClick={() => {
              if (residentId) {
                router.navigate({
                  to: '/residents/$residentId',
                  params: { residentId: residentId.toString() },
                });
              }
            }}
          >
            {fullName}
          </Typography>
          <Typography as="h5" variant="h5" weight="medium">
            {rate ? formatCurrency(rate, true) : '-'}
          </Typography>
        </div>
      );
    },
  }),
  columnHelper.accessor('lowestRate', {
    enableSorting: false,
    header: 'Lowest Rate',
    cell: ({ getValue }) => {
      const { rate, fullName, residentId } = getValue();

      return (
        <div>
          <Typography
            className={cn('opacity-85', residentId && 'hover:cursor-pointer')}
            onClick={() => {
              if (residentId) {
                router.navigate({
                  to: '/residents/$residentId',
                  params: { residentId: residentId.toString() },
                });
              }
            }}
          >
            {fullName}
          </Typography>
          <Typography as="h5" variant="h5" weight="medium">
            {rate ? formatCurrency(rate, true) : '-'}
          </Typography>
        </div>
      );
    },
  }),
  columnHelper.accessor('avgRate', {
    enableSorting: false,
    header: 'Avg Rate',
    maxSize: 200,
    cell: ({ getValue }) => {
      const rate = getValue();
      return (
        <Typography noWrap>
          {rate ? formatCurrency(rate, true) : '-'}
        </Typography>
      );
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('medianRate', {
    enableSorting: false,
    header: 'Median Rate',
    maxSize: 200,
    cell: ({ getValue }) => {
      const rate = getValue();
      return (
        <Typography noWrap>
          {rate ? formatCurrency(rate, true) : '-'}
        </Typography>
      );
    },
    footer: (info) => info.column.id,
  }),
] as ColumnDef<RatesAndRevenueTableColumns>[];

type RateAndRevenueTableProps = {
  /** Omit for the All Facilities view. */
  facilityId?: string;
  onRowHover: (
    levelOfCareData: Pick<FacilityLevelsOfCareDto, 'levelOfCare'>
  ) => void;
  onHoverEnd: () => void;
};

export const RatesAndRevenueTable: FC<RateAndRevenueTableProps> = ({
  facilityId,
  onHoverEnd,
  onRowHover,
}) => {
  const {
    data: ratesAndRevenue,
    isLoading,
    isError,
  } = useQuery(
    getFacilityRatesAndRevenueQueryOptions({
      facilityId: facilityId !== undefined ? +facilityId : undefined,
    })
  );

  const tableData = ratesAndRevenue?.data as
    | RatesAndRevenueTableColumns[]
    | undefined;

  return (
    <Table
      striped
      headerSeparated
      data={tableData}
      columns={columns}
      errored={isError}
      loading={isLoading}
      onHoverEnd={onHoverEnd}
      onRowHover={onRowHover}
    />
  );
};
